import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { axes, slackData } from "./data";

export default function Companion() {
    const svgRef = useRef();
    const correlationsRef = useRef([]);

    // UI state
    const [selectedAxis, setSelectedAxis] = useState("all");
    const [search, setSearch] = useState("");
    const [showCorrelations, setShowCorrelations] = useState(true);
    const [threshold, setThreshold] = useState(0.22);

    useEffect(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();

        const width = 1400;
        const height = 900;
        svg.attr("width", width).attr("height", height);

        // Palette pour 7 axes (harmonieuse et accessible)
        const axisColors = [
            "#3B82F6", // Bleu
            "#F59E0B", // Orange
            "#10B981", // Vert
            "#8B5CF6", // Violet
            "#0EA5E9", // Cyan
            "#EF4444", // Rouge
            "#EC4899"  // Rose (Communication)
        ];

        // Construire nœuds et liens depuis les données
        const nodes = [];
        const links = [];

        // Indexer les positions de chaque axe sur un cercle
        const angleStep = (2 * Math.PI) / axes.length;
        const centerX = width / 2;
        const centerY = height / 2;
        const axisRadius = Math.min(width, height) / 3;

        const axisPositions = {};
        axes.forEach((axe, i) => {
            const angle = i * angleStep - Math.PI / 2; // démarrer en haut
            const pos = {
                x: centerX + axisRadius * Math.cos(angle),
                y: centerY + axisRadius * Math.sin(angle)
            };
            axisPositions[axe.id] = pos;

            nodes.push({
                id: `axe:${axe.id}`,
                label: axe.nom,
                type: "axe",
                axisId: axe.id,
                color: axisColors[(axe.numero - 1) % axisColors.length],
                fx: pos.x,
                fy: pos.y
            });

            // Catégories (anneau autour de chaque axe)
            const catKeys = Object.keys(axe.categories);
            const catAngleStep = (2 * Math.PI) / Math.max(1, catKeys.length);
            const categoryRingRadius = 130;

            catKeys.forEach((catKey, j) => {
                const catAngle = j * catAngleStep;
                const cx = pos.x + categoryRingRadius * Math.cos(catAngle);
                const cy = pos.y + categoryRingRadius * Math.sin(catAngle);
                const catId = `cat:${axe.id}:${catKey}`;

                nodes.push({
                    id: catId,
                    label: catKey,
                    type: "category",
                    axisId: axe.id,
                    color: axisColors[(axe.numero - 1) % axisColors.length],
                    x: cx,
                    y: cy
                });
                links.push({ source: `axe:${axe.id}`, target: catId, strength: 0.8 });

                const items = axe.categories[catKey];
                // Disposer les items en petit nuage autour de la catégorie
                const itemAngleStep = (2 * Math.PI) / Math.max(6, items.length);
                const itemRingRadius = 70;

                items.forEach((item, k) => {
                    const ia = k * itemAngleStep;
                    const ix = cx + itemRingRadius * Math.cos(ia);
                    const iy = cy + itemRingRadius * Math.sin(ia);
                    const itemId = `item:${axe.id}:${catKey}:${k}`;

                    nodes.push({
                        id: itemId,
                        label: item.donnee,
                        meta: item,
                        type: "item",
                        axisId: axe.id,
                        color: axisColors[(axe.numero - 1) % axisColors.length],
                        x: ix,
                        y: iy
                    });
                    links.push({ source: catId, target: itemId, strength: 0.4 });
                });
            });
        });

        // Container avec zoom/pan
        const container = svg.append("g");
        const zoom = d3.zoom().scaleExtent([0.3, 2.5]).on("zoom", (event) => {
            container.attr("transform", event.transform);
        });
        svg.call(zoom);

        // Fond: cercles d'axes pour visuel "radar" data-science
        const backdrop = container.append("g");
        const backdropRings = [axisRadius + 0, axisRadius - 80, axisRadius - 160].filter(r => r > 60);
        backdropRings.forEach((r, idx) => {
            backdrop
                .append("circle")
                .attr("cx", centerX)
                .attr("cy", centerY)
                .attr("r", r)
                .attr("fill", "none")
                .attr("stroke", idx % 2 === 0 ? "#E5E7EB" : "#F3F4F6")
                .attr("stroke-width", 1.5)
                .attr("stroke-dasharray", "4,6");
        });

        // Traits reliant le centre aux positions d'axes
        const axisLines = container.append("g");
        axes.forEach((axe) => {
            axisLines
                .append("line")
                .attr("x1", centerX)
                .attr("y1", centerY)
                .attr("x2", axisPositions[axe.id].x)
                .attr("y2", axisPositions[axe.id].y)
                .attr("stroke", axisColors[(axe.numero - 1) % axisColors.length])
                .attr("stroke-opacity", 0.3)
                .attr("stroke-width", 3);
        });

        // Simulation
        const simulation = d3.forceSimulation(nodes)
            .force("link", d3.forceLink(links).id(d => d.id).distance(d => {
                if (d.type === "item") return 55;
                if (d.type === "category") return 90;
                return 120;
            }).strength(l => l.strength || 0.5))
            .force("charge", d3.forceManyBody().strength(-60))
            .force("center", d3.forceCenter(centerX, centerY))
            .force("collision", d3.forceCollide().radius(d => d.type === "item" ? 18 : d.type === "category" ? 22 : 44))
            .force("axial", d3.forceRadial(0, centerX, centerY).strength(0.02));

        // Liens
        const link = container.append("g")
            .attr("stroke", "#9CA3AF")
            .attr("stroke-opacity", 0.5)
            .selectAll("line")
            .data(links)
            .enter().append("line")
            .attr("stroke-width", 1.5);

        // Nœuds
        const node = container.append("g")
            .selectAll("g")
            .data(nodes)
            .enter().append("g")
            .attr("class", d => `node node-${d.type}`)
            .style("cursor", "pointer");

        node.append("circle")
            .attr("r", d => d.type === "item" ? 8 : d.type === "category" ? 12 : 36)
            .attr("fill", d => d.color)
            .attr("fill-opacity", d => d.type === "item" ? 0.8 : 1)
            .attr("stroke", "#fff")
            .attr("stroke-width", 2);

        // Labels courts toujours visibles pour axes et catégories
        node.filter(d => d.type !== "item")
            .append("text")
            .text(d => d.label)
            .attr("dx", d => d.type === "axe" ? 28 : 18)
            .attr("dy", 5)
            .style("font-size", d => d.type === "axe" ? "16px" : "13px")
            .style("font-weight", d => d.type === "axe" ? "700" : "600")
            .style("fill", "#111827");

        // Tooltip riche pour items
        const tooltip = d3.select("body")
            .append("div")
            .attr("class", "fixed z-50 bg-white shadow-lg border border-gray-200 rounded-md p-3 text-sm max-w-md")
            .style("pointer-events", "none")
            .style("opacity", 0);

        node.on("mouseover", function (event, d) {
            d3.select(this).select("circle").attr("r", r => (d.type === "item" ? 10 : r + 2));
            // Highlight li
            link.attr("stroke-opacity", l => (l.source.id === d.id || l.target.id === d.id ? 0.9 : 0.15));

            if (d.type === "item") {
                const meta = d.meta || {};
                tooltip
                    .style("opacity", 1)
                    .html(`
                        <div style=\"font-weight:700;margin-bottom:6px;color:#111827\">${d.label}</div>
                        ${meta.source ? `<div><span style=\"font-weight:600\">Source</span>: ${meta.source}</div>` : ""}
                        ${meta.extraction ? `<div><span style=\"font-weight:600\">Extraction</span>: ${meta.extraction}</div>` : ""}
                        ${meta.exemples_logiciels ? `<div><span style=\\\"font-weight:600\\\">Logiciels</span>: ${meta.exemples_logiciels}</div>` : ""}
                        ${meta.champs_typiques ? `<div><span style=\\\"font-weight:600\\\">Champs</span>: ${meta.champs_typiques}</div>` : ""}
                    `)
                    .style("left", `${event.pageX + 12}px`)
                    .style("top", `${event.pageY + 12}px`);
            }
        })
        .on("mousemove", function (event, d) {
            if (d.type === "item") {
                tooltip
                    .style("left", `${event.pageX + 12}px`)
                    .style("top", `${event.pageY + 12}px`);
            }
        })
        .on("mouseout", function () {
            d3.select(this).select("circle").attr("r", function () {
                const datum = d3.select(this.parentNode).datum();
                return datum.type === "item" ? 8 : datum.type === "category" ? 12 : 36;
            });
            link.attr("stroke-opacity", 0.5);
            tooltip.style("opacity", 0);
        });

        // Drag
        const drag = d3.drag()
            .on("start", (event, d) => {
                if (!event.active) simulation.alphaTarget(0.3).restart();
                d.fx = d.x;
                d.fy = d.y;
            })
            .on("drag", (event, d) => {
                d.fx = event.x;
                d.fy = event.y;
            })
            .on("end", (event, d) => {
                if (!event.active) simulation.alphaTarget(0);
                // Conserver les axes ancrés, relâcher le reste
                if (d.type !== "axe") {
                    d.fx = null;
                    d.fy = null;
                }
            });
        node.call(drag);

        // Couche de corrélations
        const correlationLayer = container.append("g").attr("class", "correlations");

        function tokenize(text) {
            if (!text) return [];
            return String(text)
                .toLowerCase()
                .normalize("NFD").replace(/[^\p{L}\p{N}]+/gu, " ")
                .split(/\s+/)
                .filter(Boolean)
                .map(t => t.length > 35 ? t.slice(0, 35) : t);
        }

        function jaccard(a, b) {
            const setA = new Set(a);
            const setB = new Set(b);
            let inter = 0;
            setA.forEach(v => { if (setB.has(v)) inter++; });
            const union = setA.size + setB.size - inter;
            return union === 0 ? 0 : inter / union;
        }

        const itemNodes = nodes.filter(n => n.type === "item");
        const itemTokens = new Map();
        itemNodes.forEach(n => {
            const m = n.meta || {};
            const concat = [n.label, m.source, m.extraction, m.exemples_logiciels, m.champs_typiques].join(" ");
            itemTokens.set(n.id, tokenize(concat));
        });

        function rebuildCorrelations() {
            correlationLayer.selectAll("path").remove();
            correlationsRef.current = [];
            if (!showCorrelations) return;

            const drawn = [];
            for (let i = 0; i < itemNodes.length; i++) {
                for (let j = i + 1; j < itemNodes.length; j++) {
                    const ni = itemNodes[i];
                    const nj = itemNodes[j];
                    // Respecter filtre axe
                    if (selectedAxis !== "all" && (ni.axisId !== selectedAxis || nj.axisId !== selectedAxis)) continue;
                    const s = jaccard(itemTokens.get(ni.id), itemTokens.get(nj.id));
                    if (s >= threshold) {
                        drawn.push({ source: ni, target: nj, score: s });
                    }
                }
            }
            correlationsRef.current = drawn;

            correlationLayer
                .selectAll("path")
                .data(drawn)
                .enter()
                .append("path")
                .attr("fill", "none")
                .attr("stroke", "#7C3AED")
                .attr("stroke-opacity", d => Math.min(0.12 + d.score * 0.6, 0.9))
                .attr("stroke-width", d => 1 + d.score * 6)
                .attr("pointer-events", "none");
        }

        // Appliquer filtres / recherche (visibilité)
        function applyFilters() {
            const q = search.trim().toLowerCase();
            node.style("display", d => {
                const axisOk = selectedAxis === "all" || d.axisId === selectedAxis;
                const qOk = q === "" || (d.label && d.label.toLowerCase().includes(q));
                return axisOk && qOk ? null : "none";
            });
            link.style("display", l => {
                const sOk = selectedAxis === "all" || (l.source.axisId === selectedAxis && l.target.axisId === selectedAxis);
                return sOk ? null : "none";
            });
        }

        rebuildCorrelations();
        applyFilters();

        // Ticker
        simulation.on("tick", () => {
            link
                .attr("x1", d => d.source.x)
                .attr("y1", d => d.source.y)
                .attr("x2", d => d.target.x)
                .attr("y2", d => d.target.y);

            node.attr("transform", d => `translate(${d.x}, ${d.y})`);

            // Mettre à jour le tracé des corrélations (courbe quadratique via le centre)
            correlationLayer.selectAll("path")
                .attr("d", d => {
                    const sx = d.source.x, sy = d.source.y;
                    const tx = d.target.x, ty = d.target.y;
                    const cx = (sx + tx) / 2 + (centerX - (sx + tx) / 2) * 0.15; // courbure vers le centre
                    const cy = (sy + ty) / 2 + (centerY - (sy + ty) / 2) * 0.15;
                    return `M${sx},${sy} Q${cx},${cy} ${tx},${ty}`;
                });
        });

        // Titres des axes près des positions d'axes
        const axisTitles = container.append("g");
        axes.forEach((axe, i) => {
            axisTitles
                .append("text")
                .attr("x", axisPositions[axe.id].x)
                .attr("y", axisPositions[axe.id].y - 38)
                .attr("text-anchor", "middle")
                .attr("font-weight", 700)
                .attr("fill", axisColors[(axe.numero - 1) % axisColors.length])
                .text(axe.nom);
        });

        // Aide rapide (coin bas gauche)
        const help = container.append("text")
            .attr("x", 24)
            .attr("y", height - 24)
            .attr("fill", "#6B7280")
            .attr("font-size", 12)
            .text("C pour cacher/montrer corrélations • +/- pour seuil");

        // Raccourcis clavier
        function onKey(e) {
            if (e.key === "c" || e.key === "C") {
                setShowCorrelations(v => !v);
            }
            if (e.key === "+" || e.key === "=") {
                setThreshold(v => Math.min(0.6, v + 0.02));
            }
            if (e.key === "-" || e.key === "_") {
                setThreshold(v => Math.max(0.06, v - 0.02));
            }
        }
        window.addEventListener("keydown", onKey);

        // Recadrer lors des changements d’UI
        const uiObserver = setInterval(() => {
            rebuildCorrelations();
            applyFilters();
        }, 300);

        return () => {
            tooltip.remove();
            window.removeEventListener("keydown", onKey);
            clearInterval(uiObserver);
        };

    }, [selectedAxis, search, showCorrelations, threshold]);

    // Exports
    function download(filename, blob) {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    }

    function exportSVG() {
        const svgNode = svgRef.current;
        const serializer = new XMLSerializer();
        const source = serializer.serializeToString(svgNode);
        const blob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
        download("graph.svg", blob);
    }

    function exportPNG() {
        const svgNode = svgRef.current;
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(svgNode);
        const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(svgBlob);

        const img = new Image();
        img.onload = function () {
            const canvas = document.createElement("canvas");
            canvas.width = svgNode.viewBox.baseVal.width || svgNode.clientWidth;
            canvas.height = svgNode.viewBox.baseVal.height || svgNode.clientHeight;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            canvas.toBlob((blob) => {
                if (blob) download("graph.png", blob);
                URL.revokeObjectURL(url);
            });
        };
        img.src = url;
    }

    function exportCorrelationsCSV() {
        const rows = [["source", "target", "score"]];
        correlationsRef.current.forEach(e => {
            rows.push([e.source.label, e.target.label, e.score.toFixed(3)]);
        });
        const csv = rows.map(r => r.map(x => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        download("correlations.csv", blob);
    }

    return (
        <div className="w-full h-screen relative">
            {/* Controls */}
            <div className="absolute top-4 left-4 z-10 bg-white/95 backdrop-blur rounded-lg border border-gray-200 shadow p-3 w-[340px]">
                <div className="text-sm font-semibold text-gray-800 mb-2">Contrôles</div>

                <div className="mb-2">
                    <label className="block text-xs text-gray-600 mb-1">Axe</label>
                    <select
                        className="w-full border rounded px-2 py-1 text-sm"
                        value={selectedAxis}
                        onChange={(e) => setSelectedAxis(e.target.value)}
                    >
                        <option value="all">Tous les axes</option>
                        {axes.map(a => (
                            <option key={a.id} value={a.id}>{a.nom}</option>
                        ))}
                    </select>
                </div>

                <div className="mb-2">
                    <label className="block text-xs text-gray-600 mb-1">Recherche</label>
                    <input
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="Rechercher un item..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <div className="mb-2">
                    <label className="block text-xs text-gray-600 mb-1">Seuil corrélation: {threshold.toFixed(2)}</label>
                    <input
                        type="range"
                        min={0.06}
                        max={0.6}
                        step={0.02}
                        value={threshold}
                        onChange={(e) => setThreshold(parseFloat(e.target.value))}
                        className="w-full"
                    />
                    <div className="flex items-center mt-2">
                        <input
                            id="toggle-corr"
                            type="checkbox"
                            checked={showCorrelations}
                            onChange={(e) => setShowCorrelations(e.target.checked)}
                            className="mr-2"
                        />
                        <label htmlFor="toggle-corr" className="text-sm text-gray-700">Afficher les corrélations</label>
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mt-3">
                    <button className="text-xs bg-gray-800 text-white rounded px-2 py-1" onClick={exportSVG}>Export SVG</button>
                    <button className="text-xs bg-gray-800 text-white rounded px-2 py-1" onClick={exportPNG}>Export PNG</button>
                    <button className="text-xs bg-gray-800 text-white rounded px-2 py-1" onClick={exportCorrelationsCSV}>CSV liens</button>
                </div>
            </div>

            <svg ref={svgRef} className="w-full h-full"></svg>
        </div>
    );
}

